import { inject, provide, ref, type InjectionKey, type Ref } from 'vue';

export type FileManagerSelectionState = {
  selectedPaths: Ref<Set<string>>;
  activePath: Ref<string | null>;
  selectOnly: (path: string) => void;
  toggle: (path: string) => void;
  extendRangeTo: (path: string, visibleOrderedPaths: string[]) => void;
  clearAll: () => void;
  pruneToVisible: (visiblePaths: Set<string>) => void;
  isSelected: (path: string) => boolean;
};

// Contiguous slice between `anchorPath` and `targetPath` in `orderedPaths`, inclusive on both
// ends and order-agnostic (works whether the anchor comes before or after the target). Falls
// back to just `[targetPath]` if the anchor can't be located in the list.
export const computeSelectionRange = (
  anchorPath: string,
  targetPath: string,
  orderedPaths: string[],
): string[] => {
  const anchorIndex = orderedPaths.indexOf(anchorPath);
  const targetIndex = orderedPaths.indexOf(targetPath);
  if (anchorIndex === -1 || targetIndex === -1) return [targetPath];

  const [start, end] =
    anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
  return orderedPaths.slice(start, end + 1);
};

const fileExplorerSelectionKey: InjectionKey<FileManagerSelectionState> =
  Symbol('fileExplorerSelection');

export const provideFileExplorerSelection = () => {
  const selectedPaths = ref<Set<string>>(new Set());
  const activePath = ref<string | null>(null);
  // Internal only: the range-selection base for the next shift-click. Does not move on a
  // shift-click itself, only on a plain click or ctrl/cmd-click.
  const anchorPath = ref<string | null>(null);

  const selectOnly = (path: string) => {
    selectedPaths.value = new Set([path]);
    activePath.value = path;
    anchorPath.value = path;
  };

  const toggle = (path: string) => {
    const next = new Set(selectedPaths.value);
    if (next.has(path)) {
      next.delete(path);
    } else {
      next.add(path);
    }
    selectedPaths.value = next;
    activePath.value = path;
    anchorPath.value = path;
  };

  const extendRangeTo = (path: string, visibleOrderedPaths: string[]) => {
    if (anchorPath.value === null || !visibleOrderedPaths.includes(anchorPath.value)) {
      selectOnly(path);
      return;
    }
    selectedPaths.value = new Set(
      computeSelectionRange(anchorPath.value, path, visibleOrderedPaths),
    );
    activePath.value = path;
  };

  const clearAll = () => {
    selectedPaths.value = new Set();
    activePath.value = null;
    anchorPath.value = null;
  };

  const pruneToVisible = (visiblePaths: Set<string>) => {
    selectedPaths.value = new Set(
      [...selectedPaths.value].filter((path) => visiblePaths.has(path)),
    );
    if (activePath.value !== null && !visiblePaths.has(activePath.value)) {
      activePath.value = null;
    }
    if (anchorPath.value !== null && !visiblePaths.has(anchorPath.value)) {
      anchorPath.value = null;
    }
  };

  const isSelected = (path: string) => selectedPaths.value.has(path);

  const state: FileManagerSelectionState = {
    selectedPaths,
    activePath,
    selectOnly,
    toggle,
    extendRangeTo,
    clearAll,
    pruneToVisible,
    isSelected,
  };
  provide(fileExplorerSelectionKey, state);
  return state;
};

export const useFileExplorerSelection = () => {
  const state = inject(fileExplorerSelectionKey);
  if (!state) {
    throw new Error(
      'useFileExplorerSelection() must be called within a component tree provided by its matching provide function',
    );
  }
  return state;
};
